import React, { useContext, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'

import styles from './style.module.scss'
import { tableLoader } from '@shared/libs/allLoader'
import { convertDt24hFormat, dateCheck } from '@shared-submodule/utils'

import CustomLink from '@shared/components/customLink'
import MyImage from '@shared/components/myImage'
import dropdown from '@assets/images/icon/down-arrow.svg'
import { Button } from 'react-bootstrap'
import StandingForm from './standingsForm'
import GlobalEventsContext from '@shared/components/global-events/GlobalEventsContext'

const ThemeTable = dynamic(() => import('@shared/components/themeTable'), { loading: () => tableLoader() })
const NoData = dynamic(() => import('@shared/components/noData'), { ssr: false })

const Standings = ({ round, standing }) => {
  const roundData = useRef(round)
  const { stateGlobalEvents } = useContext(GlobalEventsContext)

  const [collapsed, setCollapsed] = useState({})

  const [teamFormToggles, setTeamFormToggles] = useState(
    roundData?.current?.reduce((acc, round) => {
      acc[round._id] = false
      return acc
    }, {})
  )

  const toggleCollapse = (id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleTeamFormToggle = (groupId) => {
    setTeamFormToggles((prevToggles) => ({
      ...prevToggles,
      [groupId]: !prevToggles[groupId]
    }))
  }

  const getLabels = (isTeamForm) => {
    return isTeamForm ? ['No', 'Team', 'Form', ''] : ['No', 'Team', 'M', 'W', 'L', 'T', 'N/R', 'PTS', 'Net RR', '']
  }

  const table = useRef({
    nPlayed: 'M',
    nWin: 'W',
    nLoss: 'L',
    nDraw: 'T',
    nNR: 'N/R',
    nPoints: 'PTS',
    nNetrr: 'Net RR'
  })

  const standingsData = standing || []

  return (
    <>
      {roundData?.current?.length !== 0 && (
        <div className={styles.standings}>
          {roundData?.current?.map((round) => {
            const isTeamForm = teamFormToggles[round._id]
            const labels = getLabels(isTeamForm)

            return (
              <React.Fragment key={round._id}>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <h3 className="small-head">{round?.sName}</h3>
                  <div className="d-flex gap-2 align-items-center mb-2">
                    <div>Team Form</div>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={isTeamForm}
                        onChange={() => handleTeamFormToggle(round._id)}
                      />
                      <span className={styles.slider}></span>
                    </label>
                  </div>
                </div>
                <ThemeTable labels={labels}>
                  {standingsData
                    ?.filter((s) => round._id === s?.oRound?._id)
                    .map((standing, index) => (
                      <React.Fragment key={standing._id}>
                        <tr className={standing.highlight ? 'highlight' : ''}>
                          <td>{index + 1}</td>
                          <td>
                            <span className="d-none d-sm-block">
                              {standing?.oTeam?.eTagStatus === 'a' &&
                                standing?.oTeam?.oSeo?.eStatus === 'a' &&
                                standing?.oTeam?.oSeo?.sSlug ? (
                                <CustomLink href={`/${standing?.oTeam?.oSeo?.sSlug}`}>
                                  {standing?.oTeam?.sTitle}
                                </CustomLink>
                                  ) : (
                                    standing?.oTeam?.sTitle
                                  )}
                              {standing.bIsTeamQualified && ' (Q)'}
                            </span>
                            <span className="d-block d-sm-none">
                              {standing?.oTeam?.eTagStatus === 'a' &&
                                standing?.oTeam?.oSeo?.eStatus === 'a' ? (
                                <CustomLink href={`/${standing?.oTeam?.oSeo?.sSlug}`}>
                                  {standing?.oTeam?.sAbbr}
                                </CustomLink>
                                  ) : (
                                    standing?.oTeam?.sAbbr
                                  )}
                              {standing.bIsTeamQualified && ' (Q)'}
                            </span>
                          </td>
                          {isTeamForm ? (
                            <td>
                              <div className={`${styles.teamForm} d-flex gap-2 justify-content-end`}>
                                {standing?.sLastFiveMatchResult?.map((s, idx) => (
                                  <div
                                    key={idx}
                                    className={`${styles.status} ${s === 'W' ? styles.win : s === 'L' ? styles.loss : styles.none
                                      } text-center rounded-circle flex-shrink-0 small-text`}
                                  >
                                    {s}
                                  </div>
                                ))}
                              </div>
                            </td>
                          ) : (
                            Object.keys(table.current)?.map((value) => (
                              <td key={value}>{standing[value]}</td>
                            ))
                          )}
                          <td>
                            <Button
                              variant="link"
                              className={`${stateGlobalEvents?.darkModeEna === 'dark' ? styles.infoToggleDark : styles.infoToggle
                                } flex-shrink-0 ${collapsed[standing._id] && styles.active}`}
                              onClick={() => toggleCollapse(standing._id)}
                              aria-controls="example-collapse-text"
                              aria-expanded={collapsed[standing._id]}
                            >
                              <MyImage src={dropdown} alt="dropdown" />
                            </Button>
                          </td>
                        </tr>
                        {standing?.oRecentTeamMatches?.length ? (
                          <tr className={collapsed[standing._id] ? styles.collapsedRow : 'd-none'}>
                            <td colSpan={11} className={styles.infoTable}>
                              <table className="mt-1">
                                <thead>
                                  <tr className={styles.infoHead}>
                                    <td>Opponent</td>
                                    <td className="d-none d-md-table-cell">Description</td>
                                    <td>Date</td>
                                    <td>Result</td>
                                  </tr>
                                </thead>
                                <tbody>
                                  {standing?.oRecentTeamMatches?.map((match, i) => (
                                    <StandingForm key={i} match={match} />
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                </ThemeTable>
              </React.Fragment>
            )
          })}
          <div className="table-footer-note mb-3">
            Last updated on {standingsData?.length > 0 && convertDt24hFormat(dateCheck(standingsData[0]?.dUpdated))}
          </div>
        </div>
      )}
      {standingsData?.length === 0 && <NoData title={'There is no standings data for this series'} />}
    </>
  )
}

Standings.propTypes = {
  round: PropTypes.array.isRequired,
  standing: PropTypes.array.isRequired
}

export default Standings
